import { defaultHeight } from '@Common/Controls/SimpleGridLight/Editors/EditorProps';
import { TypeKind } from '@Common/Enums/Enum';

import { comparers } from '@Common/Helpers/Comparers';
import { DropDown, IListItem, Input } from '../index';
import { FilterDialog } from './FilterDialog';
import {
    ArrayItemContainsStringFilterOperation,
    BetweenFilterOperation,
    DateInIntervalFilterOperation,
    DateIntervals,
    FilterExecutor,
    FilterOperation,
    InArrayFilterOperation,
    InFilterOperation,
    NopFilterOperation,
    NullFilterExecutor,
    SimpleCompareFilterOperation,
    StringContainsFilterOperation
} from './FilterOperations';
import { FilterParameter } from './FilterParameters';
import { GridCell } from './GridCell';
import { GridColumn } from './GridColumn';
import { GridRender } from './GridRender';
import { GridRow } from './GridRow';
import { FilterType, IFilterOperation, IFilterProperty, IGridColumnProperties, IValueChangeDelegate } from './Settings';
import { Grid } from './SimpleGridLight';

export class FilterManager {
    private _provider: FilterProvider = new FilterProvider();
    private readonly filterColumns: FilterColumn[] = [];
    private readonly filterColumnsByValues: FilterColumn[] = [];
    private onFilteringHandler: () => void = $.noop;

    constructor(private readonly grid: Grid, private readonly render: GridRender) {}

    public provider(prov?: FilterProvider): FilterProvider {
        if (arguments.length === 0) {
            return this._provider;
        }
        return (this._provider = prov);
    }

    public init(columns: IGridColumnProperties[]): void {
        this.initColumns(columns);
        this.drawFilter();
    }

    public destroy(): void {
        const allFilter = [...new Set([...this.filterColumnsByValues, ...this.filterColumns])];
        for (const fc of allFilter) {
            fc.destroy();
        }
    }

    public onFiltering(fn: () => void): void {
        this.onFilteringHandler = fn;
    }

    public getFilterColumn(dataIndex: any): FilterColumn {
        return _.find(this.filterColumns, fc => fc.getColumn().dataIndex() === dataIndex) || null;
    }

    public refreshFilter(refreshData?: boolean): void {
        if (refreshData) {
            this.fillColumnsByValue();
        }
    }

    public createControl(filterType: FilterType, column: FilterColumn): FilterInlineControl {
        switch (filterType) {
            case FilterType.inPlace:
                return new SingleFilterInlineControl(this.provider(), column);
            case FilterType.dropDown:
                return new SelectFilterInlineControl(this.provider(), column);
            case FilterType.popup:
                return new PopupFilterInlineControl(this.provider(), column);
            case FilterType.byValue:
                return new SelectValuesInlineControl(this.provider(), column);
            default:
                throw 'Filter Type \'{0}\' not supported'.format(filterType);
        }
    }

    public valuesToOperationDef(values: IListItem[]): IFilterOperation[] {
        return _.map(values, item => {
            return {
                operation: commonFilter.equals,
                caption: item.value,
                // predefined values for operation
                values: {
                    compareValue: item.id
                }
            };
        });
    }

    public changeData(gridRow: GridRow, gridColumn: GridColumn): void {
        const valueColumn = this.filterColumnsByValues.filter((fc: FilterColumn) => fc.getColumn() === gridColumn);
        if (valueColumn.length) {
            this.fillColumnsByValue(valueColumn[0]);
        }
    }

    public getRowVisibility(gridRow: GridRow): boolean {
        return this.applyFilter(gridRow);
    }

    private drawFilter(): void {
        if (this.filterColumns.length == 0) {
            return;
        }

        const filterRow = $('<tr>').addClass('simpleGrid-filterRow simpleGrid-row simpleGrid-headerRow');
        const columns = this.grid.columns();

        for (let i = 0; i < columns.length; i++) {
            const filterCell = $('<th>').addClass('simpleGrid-column').appendTo(filterRow);
            const filterColumn = this.getFilterColumn(columns[i].dataIndex());
            if (filterColumn == null) {
                filterCell.addClass('simpleGrid-emptyFilterCell');
            } else {
                const inlineControl = filterColumn.filterInlineControl();
                filterCell.append(inlineControl.getControl());
            }
        }
        filterRow.appendTo($('>thead', this.render.headerTable));
    }

    private initColumns(columns: IGridColumnProperties[]): void {
        // TODO:
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            const definition: IFilterProperty = column.filter;
            if (definition) {
                const gridColumn = this.grid.getColumnByDataIndex(column.dataIndex);

                const filterType: FilterType = definition.filterType || FilterType.popup;

                const filterColumn = new FilterColumn(gridColumn, this, definition);
                this.filterColumns.push(filterColumn);
                if (filterType === FilterType.byValue) {
                    this.filterColumnsByValues.push(filterColumn);
                    filterColumn.filterInlineControl(this.createControl(FilterType.byValue, filterColumn));
                } else if (definition.values) {
                    filterColumn.filterInlineControl(this.createControl(FilterType.byValue, filterColumn));
                    const filterInlineControl = filterColumn.filterInlineControl() as SelectValuesInlineControl;
                    filterInlineControl.values(definition.values);
                } else if (definition.operations) {
                    const operations: FilterOperation[] = definition.operations.map((op: IFilterOperation) => this.createFilterOperation(op));
                    filterColumn.operations(operations);
                    filterColumn.filterInlineControl(this.createControl(filterType, filterColumn));
                }
                filterColumn.onFilterChange((fc: FilterColumn) => {
                    this.refreshFilter();
                    this.onFilteringHandler();
                    this.grid.refresh();
                });
            }
        }

        this.fillColumnsByValue();
    }

    private createFilterOperation(def: IFilterOperation): FilterOperation {
        const operation = this._provider.createOperation(def.operation);
        if (def.values) {
            operation.initValues(def.values);
        }
        if (def.caption) {
            operation.description(def.caption);
        }
        return operation;
    }

    private applyFilter(row: GridRow): boolean {
        const columns = this.filterColumns;
        for (let i = 0; i < columns.length; i++) {
            const column: FilterColumn = columns[i];
            const cellValue = row.getValue(column.getColumn());
            if (!column.getActiveFilter().filter(cellValue)) {
                return false;
            }
        }
        return true;
    }

    private fillColumnsByValue(column?: FilterColumn): void {
        const columnsByValues = column == null ? this.filterColumnsByValues : [column];
        const values = this.fillSetByValue(columnsByValues);

        for (let j = 0; j < columnsByValues.length; j++) {
            const valSet = values[j];
            const byValue: FilterColumn = columnsByValues[j];
            const valuesInlineControl = byValue.filterInlineControl() as SelectValuesInlineControl;

            const array: any[] = $.map(valSet, (value, key) => {
                return { id: key, value: value };
            });
            const comparer = byValue.getComparer();
            const operations = array.sort((tk, tk1) => comparer(tk.id, tk1.id));
            valuesInlineControl.values(operations);
        }
    }

    private fillSetByValue(columnsByValues: FilterColumn[]): any[] {
        const values = columnsByValues.map(() => {
            return {};
        });
        const rows = this.grid.rows();
        for (let i = 0; i < rows.length; i++) {
            const row: GridRow = rows[i];
            for (let j = 0; j < columnsByValues.length; j++) {
                const columnValuesSet = values[j] as any;
                const columnsByValue: FilterColumn = columnsByValues[j];
                const cell: GridCell = row.getCell(columnsByValue.getColumn());
                const cellValue: string = cell.value();
                if (columnsByValue.valuesIsArray && _.isArray(cellValue)) {
                    cellValue.forEach((val: any) => {
                        if (columnValuesSet[val] === undefined) {
                            columnValuesSet[val] = val;
                        }
                    });
                    if (cellValue.length === 0) {
                        columnValuesSet[cellValue] = '';
                    }
                } else if (columnValuesSet[cellValue] === undefined) {
                    columnValuesSet[cellValue] = cell.text();
                }
            }
        }
        return values;
    }
}

export class FilterInlineControl {
    /*protected*/
    public provider: FilterProvider;
    /*protected*/
    public column: FilterColumn;
    /*protected*/
    public onChangeHandler: IValueChangeDelegate<FilterOperation> = $.noop;

    constructor(provider: FilterProvider, column: FilterColumn) {
        this.provider = provider;
        this.column = column;
    }

    public onChange(callBack: IValueChangeDelegate<FilterOperation>): void {
        this.onChangeHandler = callBack;
    }

    public getControl(): JQuery {
        return null;
    }

    public destroy(): void {}

    public refresh(): void {}
}

class SingleFilterInlineControl extends FilterInlineControl {
    private readonly editor: IEditor;

    constructor(provider: FilterProvider, column: FilterColumn) {
        super(provider, column);
        this.editor = this.provider.createEditor(this.column.getEditorType(), { width: '100%', height: defaultHeight });

        //TODO: operations change
        const operation = this.column.operations()[0];
        this.editor.onChange((newValue: any) => {
            const parameters: Array<FilterParameter> = operation.getParameters();
            const parameter: FilterParameter = parameters[0];
            parameter.value(newValue);
            this.onChangeHandler(operation);
        });
    }

    public destroy(): void {
        this.editor.destroy();
        super.destroy();
    }

    public getControl(): JQuery {
        return this.editor.getControl();
    }
}

class SelectValuesInlineControl extends FilterInlineControl {
    private readonly dropDown: DropDown;
    private inOperation: InFilterOperation;

    constructor(provider: FilterProvider, column: FilterColumn) {
        super(provider, column);

        this.inOperation = column.valuesIsArray ? new InArrayFilterOperation() : new InFilterOperation(column.defaults);
        this.dropDown = new DropDown({
            width: '100%',
            sortable: false,
            showClearAllButton: true,
            checkable: true,
            height: defaultHeight
        });
        this.dropDown.onChange((vals: IListItem[]) => this.changeFilterBox(vals));
    }

    public values(values: IListItem[]): void {
        const dictionary = values.searchTree();
        const checked = this.inOperation.selectedValues.map(id => dictionary[id]).filter(val => val != null);
        this.dropDown.setItems(values);
        this.dropDown.selectedItems(checked);
        if (checked.length !== 0) {
            this.changeFilterBox(checked);
        }
    }

    public destroy(): void {
        this.dropDown.destroy();
        super.destroy();
    }

    public getControl(): JQuery {
        return this.dropDown.getControl();
    }

    private changeFilterBox(values: IListItem[]): void {
        this.inOperation.selectedValues = values.map(dropItem => dropItem.id);
        this.onChangeHandler(this.inOperation);
    }
}

class SelectFilterInlineControl extends FilterInlineControl {
    private readonly dropDown: DropDown;
    private lastOperation: FilterOperation;

    constructor(provider: FilterProvider, column: FilterColumn) {
        super(provider, column);
        const allOperation = (this.lastOperation = new NopFilterOperation());
        this.dropDown = new DropDown({ width: '100%', sortable: false, height: defaultHeight });
        this.dropDown.onItemClick((vals: any) => {
            const index = vals.id;
            this.lastOperation = index === -1 ? allOperation : column.operations()[index];
            if (this.lastOperation.isRequiredValues()) {
                this.showSetValueDialog(this.lastOperation);
            } else {
                this.onChangeHandler(this.lastOperation);
            }
        });
        this.refresh();
    }

    public getControl(): JQuery {
        return this.dropDown.getControl();
    }

    public refresh(): void {
        const operations = this.column.operations() || [];
        let selectedIndex = -1;
        const dropDownItem = operations.map((op: FilterOperation, index: number) => {
            if (this.lastOperation.equals(op)) {
                selectedIndex = index;
            }
            return { id: index, value: op.description() };
        });

        const all = { id: -1, value: strings.gridControl.filterAllValue };
        dropDownItem.insert(0, all);
        this.dropDown.setItems(dropDownItem);
        this.dropDown.selectedItems([dropDownItem[selectedIndex + 1]]);
    }

    public showSetValueDialog(operation: FilterOperation): void {
        const fd: FilterDialog = new FilterDialog(this.column, operation, this.provider);
        fd.onOk(() => {
            this.onChangeHandler(operation);
        });
        fd.show();
    }

    public destroy(): void {
        this.dropDown.destroy();
        super.destroy();
    }
}

class PopupFilterInlineControl extends FilterInlineControl {
    constructor(provider: FilterProvider, column: FilterColumn) {
        super(provider, column);
    }
}

export interface IFilterDataCell {
    value();

    text();
}

export interface IFilterData {
    dataIndex();

    getCell(dataIndex): IFilterDataCell;
}

export type IComparer = (x: any, y: any) => number;

export class FilterColumn {
    private control: FilterInlineControl;
    private activeFilter: FilterExecutor = new NullFilterExecutor();
    private onFilterChangeHandler: IValueChangeDelegate<FilterColumn> = $.noop;
    private opers: FilterOperation[];

    constructor(private readonly column: GridColumn, private readonly filterManager: FilterManager, private readonly definition: IFilterProperty) {}

    public get defaults(): string[] {
        return this.definition.defaults ?? [];
    }

    public get valuesIsArray(): boolean {
        return this.definition.valuesIsArray || false;
    }

    public getColumn(): GridColumn {
        return this.column;
    }

    public filterInlineControl(ctrl?: FilterInlineControl): FilterInlineControl {
        if (arguments.length === 0) {
            return this.control;
        }

        this.control = ctrl;
        this.control.onChange((op: FilterOperation) => {
            this.activeFilter = op.execute(this.getComparer());
            this.onFilterChangeHandler(this);
        });
        return this.control;
    }

    public getEditorType(): string {
        return this.definition.type;
    }

    public getComparer(): IComparer {
        return this.column.getComparer() || comparers.comparer;
    }

    public getActiveFilter(): FilterExecutor {
        return this.activeFilter;
    }

    public operations(val?: FilterOperation[]): FilterOperation[] {
        if (arguments.length === 0) {
            return this.opers;
        } else {
            this.opers = val;
            if (this.control) {
                this.control.refresh();
            }
            return this.opers;
        }
    }

    public onFilterChange(callBack: IValueChangeDelegate<FilterColumn>): void {
        this.onFilterChangeHandler = callBack;
    }

    public destroy(): void {
        this.control && this.control.destroy();
    }
}

export class FilterProvider {
    public createOperation(key: CommonFilters): FilterOperation {
        if (!key) {
            throw new Error('key is empty');
        }

        switch (key) {
            case commonFilter.equals:
                return new SimpleCompareFilterOperation('=', [0]);
            case commonFilter.less:
                return new SimpleCompareFilterOperation('<', [-1]);
            case commonFilter.greater:
                return new SimpleCompareFilterOperation('>', [1]);
            case commonFilter.equalsLess:
                return new SimpleCompareFilterOperation('<=', [-1, 0]);
            case commonFilter.equalsGreater:
                return new SimpleCompareFilterOperation('>=', [0, 1]);
            case commonFilter.between:
                return new BetweenFilterOperation();
            case commonFilter.stringFilter.contains:
                return new StringContainsFilterOperation();
            case commonFilter.arrayFilter.itemContainsString:
                return new ArrayItemContainsStringFilterOperation();
        }
        const datefilter = 'dateFilter.';

        if (key.startsWith(datefilter)) {
            const index = this.findDateFilterIndex(key.substring(datefilter.length));
            if (index >= 0) {
                const interval = Math.floor(index / 3);
                const shift = (index % 3) - 1;
                return new DateInIntervalFilterOperation(this.getInervalByIndex(interval), shift);
            }
        }
        throw 'key \'{0}\' not found'.format(key);
    }

    public createEditor(type: string, param?: any): IEditor {
        let i: Input;
        if (type === filterTypes.date) {
            i = new Input(
                $.extend(
                    {
                        type: TypeKind.dateTime,
                        showTime: false
                    },
                    param || {}
                )
            );
        } else {
            i = new Input($.extend({ type: type, showTime: true }, param || {}));
        }
        return buildInputAdapter(i);
    }

    private getInervalByIndex(index: number): string {
        switch (index) {
            case 0:
                return DateIntervals.day;
            case 1:
                return DateIntervals.week;
            case 2:
                return DateIntervals.month;
        }
        throw new RangeError(`Index '${index}' should be [0,2]`);
    }

    private findDateFilterIndex(key: string): number {
        let index = 0;
        for (const p in commonFilter.dateFilter) {
            if (p == key) {
                return index;
            }
            index++;
        }
        return -1;
    }
}

function buildInputAdapter(input: Input): IEditor {
    return {
        value(v?: any) {
            if (arguments.length === 0) {
                return input.getValue();
            }
            input.setValue(v);
            return v;
        },
        adjust(w?, h?) {
            input.adjust(w, h);
        },
        destroy() {
            input.destroy();
        },
        getControl: () => input.getControl(),
        onChange(fb: IValueChangeDelegate<any>): void {
            input.onChange(() => {
                fb(input.getValue());
            });
        }
    };
}

export interface IEditor {
    value(value?: any): any;

    adjust(w?: any, h?: any): void;

    getControl(): JQuery;

    destroy(): void;

    onChange(callBack: IValueChangeDelegate<any>): void;
}

export const filterTypes = {
    string: TypeKind.string,
    dateTime: TypeKind.dateTime,
    date: 'date'
};

export const commonFilter = {
    equals: 'equals',
    less: 'less',
    greater: 'greater',
    equalsLess: 'equalsLess',
    equalsGreater: 'equalsGreater',
    between: 'between',
    stringFilter: {
        contains: 'stringFilter.contains'
    },
    dateFilter: {
        yesterday: 'dateFilter.yesterday',
        today: 'dateFilter.today',
        tomorrow: 'dateFilter.tomorrow',
        lastWeek: 'dateFilter.lastWeek',
        thisWeek: 'dateFilter.thisWeek',
        nextWeek: 'dateFilter.nextWeek',
        lastMonth: 'dateFilter.lastMonth',
        thisMonth: 'dateFilter.thisMonth',
        nextMonth: 'dateFilter.nextMonth'
    },
    arrayFilter: {
        itemContainsString: 'arrayFilter.itemContainsString'
    }
} as const;

type ValueOf<T> = {
    [TKey in keyof T]: T[TKey] extends string ? T[TKey] : ValueOf<T[TKey]>;
}[keyof T];

export type CommonFilters = ValueOf<typeof commonFilter>;

const filtersResources = strings.simpleGridLight.filters;
export const dateCommonOperations: IFilterOperation[] = [
    { operation: commonFilter.equals, caption: filtersResources.equals },
    { operation: commonFilter.less, caption: filtersResources.before },
    { operation: commonFilter.greater, caption: filtersResources.after },
    { operation: commonFilter.between },
    { operation: commonFilter.dateFilter.yesterday, caption: filtersResources.yesterday },
    { operation: commonFilter.dateFilter.today, caption: filtersResources.today },
    { operation: commonFilter.dateFilter.tomorrow, caption: filtersResources.tomorrow },
    { operation: commonFilter.dateFilter.lastWeek, caption: filtersResources.lastWeek },
    { operation: commonFilter.dateFilter.thisWeek, caption: filtersResources.thisWeek },
    { operation: commonFilter.dateFilter.nextWeek, caption: filtersResources.nextWeek },
    { operation: commonFilter.dateFilter.lastMonth, caption: filtersResources.lastMonth },
    { operation: commonFilter.dateFilter.thisMonth, caption: filtersResources.thisMonth },
    { operation: commonFilter.dateFilter.nextMonth, caption: filtersResources.nextMonth }
];

export const dateCommonOperationsNoFuture: IFilterOperation[] = [
    { operation: commonFilter.equals, caption: filtersResources.equals },
    { operation: commonFilter.less, caption: filtersResources.before },
    { operation: commonFilter.greater, caption: filtersResources.after },
    { operation: commonFilter.between },
    { operation: commonFilter.dateFilter.yesterday, caption: filtersResources.yesterday },
    { operation: commonFilter.dateFilter.today, caption: filtersResources.today },
    { operation: commonFilter.dateFilter.lastWeek, caption: filtersResources.lastWeek },
    { operation: commonFilter.dateFilter.thisWeek, caption: filtersResources.thisWeek },
    { operation: commonFilter.dateFilter.lastMonth, caption: filtersResources.lastMonth },
    { operation: commonFilter.dateFilter.thisMonth, caption: filtersResources.thisMonth }
];
